import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CalendlyModal } from '@/components/CalendlyModal';
import { useLanguage } from '@/i18n/LanguageContext';
import { useState, useEffect, useMemo } from 'react';
import { useParallax, useMouseParallax } from '@/hooks/useParallax';
import { useIsMobile } from '@/hooks/use-mobile';
const heroDatacenterBg = '/images/hero-datacenter-bg.jpg';

// Digital Rain Line Component (desktop only)
const DigitalRainLine = ({ delay, left, duration, height }: { delay: number; left: string; duration: number; height: string }) => (
  <div
    className="absolute top-0 w-[1px] opacity-0"
    style={{
      left,
      height,
      background: 'linear-gradient(180deg, transparent 0%, rgba(0, 158, 224, 0.8) 20%, rgba(19, 181, 230, 0.6) 50%, rgba(0, 158, 224, 0.4) 80%, transparent 100%)',
      animation: `digitalRain ${duration}s linear infinite`,
      animationDelay: `${delay}s`,
      boxShadow: '0 0 8px rgba(0, 158, 224, 0.5), 0 0 15px rgba(0, 158, 224, 0.3)',
    }}
  />
);

// Glowing dot (desktop only)
const GlowDot = ({ delay, left, top }: { delay: number; left: string; top: string }) => (
  <div
    className="absolute w-1 h-1 rounded-full opacity-0"
    style={{
      left,
      top,
      background: 'radial-gradient(circle, rgba(19, 181, 230, 1) 0%, rgba(0, 158, 224, 0.5) 50%, transparent 100%)',
      animation: `dotPulse 3s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      boxShadow: '0 0 6px rgba(19, 181, 230, 0.8), 0 0 12px rgba(0, 158, 224, 0.4)',
    }}
  />
);

// Static grid pattern for mobile (replaces animated rain + dots)
// Using forwardRef to avoid React warning when parent passes ref
const StaticGridPattern = React.forwardRef<HTMLDivElement>((_, ref) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Static dot grid via radial-gradient — zero animation, same cyan feel */}
    <div
      className="absolute inset-0 opacity-[0.18]"
      style={{
        backgroundImage: `
          radial-gradient(circle 1px, rgba(0, 158, 224, 0.6) 0%, transparent 100%)
        `,
        backgroundSize: '40px 40px',
        backgroundPosition: 'center',
      }}
    />
    {/* Static horizontal perspective lines */}
    <div className="absolute bottom-0 left-0 right-0 h-[50%] opacity-25">
      {Array.from({ length: 6 }, (_, i) => (
        <div
          key={i}
          className="absolute left-0 right-0 h-[1px]"
          style={{
            bottom: `${i * 14}%`,
            background: 'linear-gradient(90deg, transparent 0%, rgba(0, 158, 224, 0.4) 30%, rgba(0, 158, 224, 0.4) 70%, transparent 100%)',
            transform: `scaleX(${1 - i * 0.1})`,
            opacity: 1 - i * 0.12,
          }}
        />
      ))}
    </div>
    {/* Static vertical perspective lines */}
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[50%] opacity-15">
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i - 3.5) * 10;
        return (
          <div
            key={i}
            className="absolute bottom-0 left-1/2 h-full w-[1px] origin-bottom"
            style={{
              background: 'linear-gradient(0deg, rgba(0, 158, 224, 0.5) 0%, transparent 80%)',
              transform: `translateX(-50%) rotate(${angle}deg)`,
            }}
          />
        );
      })}
    </div>
  </div>
));
StaticGridPattern.displayName = 'StaticGridPattern';

export function Hero() {
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  
  // Disable parallax effects on mobile
  const scrollOffset = useParallax({ speed: 0.3, disabled: isMobile });
  const mousePosition = useMouseParallax(isMobile ? 0 : 0.015);

  // Generate rain lines & dots only on desktop
  const [rainLines, setRainLines] = useState<Array<{ id: number; left: string; delay: number; duration: number; height: string }>>([]);
  const [glowDots, setGlowDots] = useState<Array<{ id: number; left: string; top: string; delay: number }>>([]);

  useEffect(() => {
    if (isMobile) return; // Skip entirely on mobile
    
    const lines = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: Math.random() * 8,
      duration: 3 + Math.random() * 4,
      height: `${30 + Math.random() * 40}%`,
    }));
    setRainLines(lines);

    const dots = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: Math.random() * 5,
    }));
    setGlowDots(dots);
  }, [isMobile]);

  return (
    <section className="min-h-screen flex items-center justify-center pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-24 md:pb-32 relative overflow-hidden">
      {/* Dark Premium Background - Deep Blue/Black */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 120% 80% at 50% 100%, rgba(0, 69, 129, 0.4) 0%, transparent 60%),
            radial-gradient(ellipse 100% 60% at 50% 0%, rgba(0, 98, 172, 0.2) 0%, transparent 50%),
            linear-gradient(180deg, 
              hsl(220, 30%, 6%) 0%, 
              hsl(215, 35%, 8%) 30%, 
              hsl(210, 40%, 10%) 60%,
              hsl(220, 30%, 8%) 100%
            )
          `
        }}
      />

      {/* Datacenter Background Image - Watermark — skip on mobile (12% opacity = invisible, saves 1.3MB) */}
      {!isMobile && (
        <picture>
          <source srcSet="/images/hero-datacenter-bg-optimized.avif" type="image/avif" />
          <source srcSet="/images/hero-datacenter-bg-optimized.webp" type="image/webp" />
          <img
            src={heroDatacenterBg}
            alt=""
            role="presentation"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.12, mixBlendMode: 'screen' }}
          />
        </picture>
      )}
      
      {/* Horizon Glow Effect - Central Light */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-[40%] pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% 100%, 
              rgba(0, 158, 224, 0.15) 0%, 
              rgba(0, 98, 172, 0.08) 40%,
              transparent 70%
            )
          `,
        }}
      />

      {/* Mobile: static CSS pattern | Desktop: animated rain + dots */}
      {isMobile ? (
        <StaticGridPattern />
      ) : (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {rainLines.map((line) => (
            <DigitalRainLine
              key={line.id}
              left={line.left}
              delay={line.delay}
              duration={line.duration}
              height={line.height}
            />
          ))}
          
          {glowDots.map((dot) => (
            <GlowDot
              key={dot.id}
              left={dot.left}
              top={dot.top}
              delay={dot.delay}
            />
          ))}

          {/* Horizontal grid lines for perspective */}
          <div className="absolute bottom-0 left-0 right-0 h-[50%] overflow-hidden opacity-30">
            {Array.from({ length: 8 }, (_, i) => (
              <div
                key={i}
                className="absolute left-0 right-0 h-[1px]"
                style={{
                  bottom: `${i * 12}%`,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(0, 158, 224, 0.4) 30%, rgba(0, 158, 224, 0.4) 70%, transparent 100%)',
                  transform: `scaleX(${1 - i * 0.08})`,
                  opacity: 1 - i * 0.1,
                }}
              />
            ))}
          </div>

          {/* Vertical perspective lines from center */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[50%] overflow-hidden opacity-20">
            {Array.from({ length: 12 }, (_, i) => {
              const angle = (i - 5.5) * 8;
              return (
                <div
                  key={i}
                  className="absolute bottom-0 left-1/2 h-full w-[1px] origin-bottom"
                  style={{
                    background: 'linear-gradient(0deg, rgba(0, 158, 224, 0.5) 0%, transparent 80%)',
                    transform: `translateX(-50%) rotate(${angle}deg)`,
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Ambient glow effects — no filter:blur on mobile, use pre-blurred radial gradient instead */}
      {!isMobile && (
        <>
          <div 
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
            style={{ 
              background: 'radial-gradient(circle, rgba(0, 158, 224, 0.08) 0%, transparent 60%)',
              filter: 'blur(80px)',
              transform: `translate(${mousePosition.x * -0.2}px, ${mousePosition.y * -0.2}px)`,
            }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{ 
              background: 'radial-gradient(circle, rgba(0, 98, 172, 0.1) 0%, transparent 60%)',
              filter: 'blur(60px)',
              transform: `translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.15}px)`,
            }}
          />
        </>
      )}
      {/* Mobile: static ambient glow without filter:blur (radial gradient already looks blurred) */}
      {isMobile && (
        <>
          <div 
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
            style={{ 
              background: 'radial-gradient(circle, rgba(0, 158, 224, 0.06) 0%, transparent 50%)',
            }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{ 
              background: 'radial-gradient(circle, rgba(0, 98, 172, 0.08) 0%, transparent 50%)',
            }}
          />
        </>
      )}

      <div 
        className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        style={!isMobile ? { transform: `translateY(${scrollOffset * 0.2}px)` } : undefined}
      >
        {/* Centered Content - Clean & Premium */}
        <div className="max-w-6xl mx-auto text-center space-y-5 sm:space-y-6 md:space-y-8">

          {/* Main Heading - Large & Impactful */}
          <div className="space-y-2 sm:space-y-3 animate-fade-in">
            <h1 className="font-display font-bold text-white leading-[1.15] sm:leading-[1.1] tracking-tight px-2 sm:px-0 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
              <span className="block text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl md:whitespace-nowrap">
                {t.hero.newTitle}
              </span>
              <span className="block mt-4 sm:mt-5 md:mt-6 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl bg-gradient-to-r from-accent via-secondary to-cyan-300 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                {t.hero.newTitleAccent}
                {t.hero.newTitleAccent2 && <br />}
                {t.hero.newTitleAccent2}
              </span>
            </h1>
          </div>

          {/* Subtitle - Clean */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/70 max-w-2xl mx-auto leading-relaxed animate-fade-in px-2 sm:px-0" style={{ animationDelay: '100ms' }}>
            {t.hero.newDescription}
          </p>

          {/* CTA - Refined subtle button */}
          <div className="flex justify-center pt-4 sm:pt-6 animate-fade-in" style={{ animationDelay: '200ms' }}>
            <button 
              className="relative bg-transparent text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold group transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(0,158,224,0.3)] overflow-hidden"
              onClick={() => setIsCalendlyOpen(true)}
            >
              {/* Gradient border */}
              <span className="absolute inset-0 rounded-full p-[2px] bg-gradient-to-r from-accent via-secondary to-cyan-300 bg-[length:200%_auto] animate-gradient">
                <span className="block w-full h-full rounded-full bg-navy-dark" />
              </span>
              <span className="relative flex items-center gap-2.5">
                {t.hero.ctaBook}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
          </div>

          {/* Subtle trust indicators */}
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-3 sm:gap-6 md:gap-8 pt-6 sm:pt-8 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center gap-2 text-white/50">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.6)]" />
              <span className="text-xs sm:text-sm">{t.hero.capabilities.cloudDesc}</span>
            </div>
            <div className="flex items-center gap-2 text-white/50">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.6)]" style={{ animationDelay: '0.5s' }} />
              <span className="text-xs sm:text-sm">{t.hero.differentiator.split('•')[1]?.trim() || 'Datacenter Tier IV'}</span>
            </div>
            <div className="flex items-center gap-2 text-white/50">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.6)]" style={{ animationDelay: '1s' }} />
              <span className="text-xs sm:text-sm">{t.hero.capabilities.securityDesc}</span>
            </div>
          </div>
        </div>
      </div>

      <CalendlyModal 
        isOpen={isCalendlyOpen} 
        onClose={() => setIsCalendlyOpen(false)} 
      />
    </section>
  );
}
